<template>
    <div class="profile-page">
        <PageHeader icon="fas fa-shield-alt">
            <template #title>Ubah Password</template>
            <template #subtitle>Perbarui password secara berkala untuk menjaga keamanan akun.</template>
        </PageHeader>

        <Card card-class="profile-content-card profile-form-card" body-class="profile-content-card__body">
            <template #body>
                <b-form class="profile-form" @submit.prevent="formOnSubmit" style="display: flex; flex-direction: column; width: 100%;">
                    <div class="profile-form-layout profile-form-layout--password" style="width: 100%;">
                        <aside class="profile-security-panel">
                            <div class="profile-avatar profile-avatar--medium">
                                <img :src="profilePhoto" :alt="`Foto profil ${displayValue(data.name)}`" @error="useDefaultAvatar" />
                                <span class="profile-avatar__status" title="Akun aktif"></span>
                            </div>
                            <h3>{{ displayValue(data.name) }}</h3>
                            <p>{{ displayValue(data.email) }}</p>

                            <div class="profile-security-note" style="display: flex; align-items: flex-start; gap: 12px; padding: 14px; text-align: left;">
                                <span style="display: grid; flex: 0 0 34px; width: 34px; height: 34px; min-width: 34px; place-items: center; border-radius: 9px; background: rgba(21, 151, 142, 0.11); color: var(--profile-teal); font-size: 14px; margin-right: 10px;">
                                    <i class="fas fa-lock"></i>
                                </span>
                                <div style="flex: 1 1 auto; min-width: 0;">
                                    <strong style="display: block; color: var(--profile-ink); font-size: 12px; line-height: 1.35;">Password yang aman</strong>
                                    <small style="display: block; margin-top: 4px; color: var(--profile-muted); font-size: 11px; line-height: 1.45;">Gunakan minimal 10 karakter dan hindari password yang mudah ditebak.</small>
                                </div>
                            </div>
                        </aside>

                        <div class="profile-form-main">
                            <div class="profile-section-heading">
                                <span class="profile-section-heading__icon"><i class="fas fa-key"></i></span>
                                <div>
                                    <h3>Keamanan Akun</h3>
                                    <p>Masukkan password lama sebelum membuat password baru.</p>
                                </div>
                            </div>

                            <div class="profile-password-fields">
                                <!-- Password Lama -->
                                <b-form-group class="profile-field" label="Password Lama" label-for="oldPassword">
                                    <div class="profile-field__control" style="position: relative; width: 100%;">
                                        <i class="fas fa-lock" style="position: absolute; left: 16px; top: 50%; transform: translateY(-50%); z-index: 5; pointer-events: none; color: #8aa0b5; font-size: 14px;"></i>
                                        <b-form-input
                                            id="oldPassword"
                                            v-model="form.old_password"
                                            :type="visibility.old"
                                            required
                                            placeholder="Masukkan password lama"
                                            style="padding-left: 48px !important; padding-right: 48px !important; height: 46px !important; border-radius: 10px !important;"
                                        ></b-form-input>
                                        <button
                                            type="button"
                                            :aria-label="visibility.old === 'password' ? 'Tampilkan password lama' : 'Sembunyikan password lama'"
                                            @click="toggleVisibility('old')"
                                            style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%); z-index: 5; background: transparent; border: 0; padding: 4px 8px; color: #7890a7; cursor: pointer;"
                                        >
                                            <i :class="visibility.old === 'password' ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                                        </button>
                                    </div>
                                    <small v-if="error.old_password" class="text-danger">{{ error.old_password }}</small>
                                </b-form-group>

                                <!-- Password Baru -->
                                <b-form-group class="profile-field" label="Password Baru" label-for="newPassword">
                                    <div class="profile-field__control" style="position: relative; width: 100%;">
                                        <i
                                            class="fas fa-key"
                                            :style="{
                                                position: 'absolute',
                                                left: '16px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                zIndex: 5,
                                                pointerEvents: 'none',
                                                fontSize: '14px',
                                                color: isPasswordLengthValid ? '#28a745' : '#8aa0b5'
                                            }"
                                        ></i>
                                        <b-form-input
                                            id="newPassword"
                                            v-model="form.new_password"
                                            :type="visibility.newPassword"
                                            required
                                            placeholder="Masukkan password baru"
                                            :style="{
                                                paddingLeft: '48px !important',
                                                paddingRight: '48px !important',
                                                height: '46px !important',
                                                borderRadius: '10px !important',
                                                borderColor: isPasswordLengthValid ? '#28a745 !important' : '',
                                                backgroundColor: isPasswordLengthValid ? '#fafffb !important' : '',
                                                boxShadow: isPasswordLengthValid ? '0 0 0 3px rgba(40, 167, 69, 0.15) !important' : ''
                                            }"
                                        ></b-form-input>
                                        <button
                                            type="button"
                                            :aria-label="visibility.newPassword === 'password' ? 'Tampilkan password baru' : 'Sembunyikan password baru'"
                                            @click="toggleVisibility('newPassword')"
                                            style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%); z-index: 5; background: transparent; border: 0; padding: 4px 8px; color: #7890a7; cursor: pointer;"
                                        >
                                            <i :class="visibility.newPassword === 'password' ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                                        </button>
                                    </div>
                                    <!-- Indikator Panjang Password -->
                                    <div class="mt-2 d-flex align-items-center flex-wrap">
                                        <small
                                            :class="isPasswordLengthValid ? 'text-success font-weight-bold' : 'text-muted'"
                                            style="font-size: 12px; display: inline-flex; align-items: center;"
                                        >
                                            <i :class="isPasswordLengthValid ? 'fas fa-check-circle text-success' : 'far fa-circle text-muted'" class="mr-1"></i>
                                            Panjang password: {{ (form.new_password || '').length }} karakter 
                                            <span v-if="isPasswordLengthValid">&nbsp;(Pas - Memenuhi syarat minimal 10 karakter)</span>
                                            <span v-else>&nbsp;(Minimal 10 karakter)</span>
                                        </small>
                                        <span
                                            v-if="isPasswordLengthValid"
                                            class="badge badge-success ml-2 py-1 px-2"
                                            style="background-color: #28a745; color: #fff; font-size: 10px; font-weight: 700; border-radius: 6px;"
                                        >
                                            ✓ Format Pas
                                        </span>
                                    </div>
                                    <small v-if="error.new_password" class="text-danger">{{ error.new_password }}</small>
                                </b-form-group>

                                <!-- Konfirmasi Password Baru -->
                                <b-form-group class="profile-field" label="Konfirmasi Password Baru" label-for="passwordConfirmation">
                                    <div class="profile-field__control" style="position: relative; width: 100%;">
                                        <i
                                            class="fas fa-check-circle"
                                            :style="{
                                                position: 'absolute',
                                                left: '16px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                zIndex: 5,
                                                pointerEvents: 'none',
                                                fontSize: '14px',
                                                color: isPasswordMatch ? '#28a745' : '#8aa0b5'
                                            }"
                                        ></i>
                                        <b-form-input
                                            id="passwordConfirmation"
                                            v-model="form.new_password_confirmation"
                                            :type="visibility.confirmation"
                                            required
                                            placeholder="Ketik ulang password baru"
                                            :style="{
                                                paddingLeft: '48px !important',
                                                paddingRight: '48px !important',
                                                height: '46px !important',
                                                borderRadius: '10px !important',
                                                borderColor: isPasswordMatch ? '#28a745 !important' : '',
                                                backgroundColor: isPasswordMatch ? '#fafffb !important' : '',
                                                boxShadow: isPasswordMatch ? '0 0 0 3px rgba(40, 167, 69, 0.15) !important' : ''
                                            }"
                                        ></b-form-input>
                                        <button
                                            type="button"
                                            :aria-label="visibility.confirmation === 'password' ? 'Tampilkan konfirmasi password' : 'Sembunyikan konfirmasi password'"
                                            @click="toggleVisibility('confirmation')"
                                            style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%); z-index: 5; background: transparent; border: 0; padding: 4px 8px; color: #7890a7; cursor: pointer;"
                                        >
                                            <i :class="visibility.confirmation === 'password' ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                                        </button>
                                    </div>
                                    <!-- Indikator Kecocokan Password -->
                                    <div class="mt-2" v-if="form.new_password_confirmation">
                                        <small
                                            :class="isPasswordMatch ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'"
                                            style="font-size: 12px; display: inline-flex; align-items: center;"
                                        >
                                            <i :class="isPasswordMatch ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-danger'" class="mr-1"></i>
                                            {{ isPasswordMatch ? 'Konfirmasi password cocok' : 'Konfirmasi password belum sama' }}
                                        </small>
                                    </div>
                                    <small v-if="error.new_password_confirmation" class="text-danger">{{ error.new_password_confirmation }}</small>
                                </b-form-group>
                            </div>
                        </div>
                    </div>

                    <div class="profile-form-actions" style="display: flex; justify-content: flex-end; align-items: center; gap: 12px; padding: 18px 28px; border-top: 1px solid var(--profile-border); background: #fbfcfe; width: 100%;">
                        <b-button class="profile-page__secondary-action" @click="$router.push('/profile/show')" style="min-height: 42px; display: inline-flex; align-items: center;">Batal</b-button>
                        <b-button type="submit" class="profile-page__primary-action" style="min-height: 42px; display: inline-flex; align-items: center;">
                            <i class="fas fa-save mr-2"></i>Simpan Password
                        </b-button>
                    </div>
                </b-form>
            </template>
        </Card>
    </div>
</template>

<script>
import Card from "@/view/content/Card.vue";
import module from "@/core/modules/CrudModule.js";
import validation from "@/core/modules/ValidationModule.js";
import { getUser } from "@/core/services/jwt.service.js";

export default {
    components: { Card },
    data() {
        return {
            data: getUser() || {},
            defaultAvatar: "/media/users/user-default.svg",
            form: {
                old_password: "",
                new_password: "",
                new_password_confirmation: "",
                _method: "put",
            },
            error: {
                old_password: "",
                new_password: "",
                new_password_confirmation: "",
            },
            visibility: {
                old: "password",
                newPassword: "password",
                confirmation: "password",
            },
        };
    },
    computed: {
        profilePhoto() {
            return this.data.photo || this.defaultAvatar;
        },
        isPasswordLengthValid() {
            return (this.form.new_password || "").length >= 10;
        },
        isPasswordMatch() {
            return Boolean(
                this.form.new_password &&
                this.form.new_password_confirmation &&
                this.form.new_password === this.form.new_password_confirmation
            );
        },
    },
    methods: {
        displayValue(value) {
            return value || "Belum tersedia";
        },
        useDefaultAvatar(event) {
            if (!event.target.src.endsWith(this.defaultAvatar)) event.target.src = this.defaultAvatar;
        },
        toggleVisibility(field) {
            this.visibility[field] = this.visibility[field] === "password" ? "text" : "password";
        },
        async formOnSubmit() {
            const response = await module.submit(this.form, `profile/change-password/${this.data.user_id}`);
            if (response.state === "error") {
                this.error = validation.setValidationError(this.error, response.error.errors);
                return;
            }
            try {
                const cur = getUser() || {};
                cur.must_change_password = false;
                window.localStorage.setItem("user", JSON.stringify(cur));
            } catch (e) {}
            Swal.fire(response.success.title, response.success.message, "success");
            this.$router.push({ name: "dashboard" });
        },
    },
};
</script>

<style lang="scss" src="@/assets/sass/pages/_profile-pages.scss"></style>
